/**
 * The announcement preview (TMMPDM-0110): the body's small Markdown subset (`markdownSubset.ts`)
 * rendered as React elements only. Never `dangerouslySetInnerHTML`, never an image, a frame or a
 * script: raw HTML typed in a body shows as text. `{productName}` shows as typed; the member sees
 * their product's name in its place.
 */
import { Fragment, type ReactNode } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { parseMarkdownSubset, type MarkdownInline } from './markdownSubset';

function renderInline(nodes: MarkdownInline[]): ReactNode {
  return nodes.map((node, index) => {
    switch (node.type) {
      case 'text':
        return <Fragment key={index}>{node.text}</Fragment>;
      case 'strong':
        return <strong key={index}>{renderInline(node.children)}</strong>;
      case 'em':
        return <em key={index}>{renderInline(node.children)}</em>;
      case 'code':
        return (
          <Box
            key={index}
            component="code"
            sx={(theme) => ({ ...theme.typography.mono, fontSize: '0.875em' })}
          >
            {node.text}
          </Box>
        );
      case 'link':
        return (
          <Link key={index} href={node.href} target="_blank" rel="noopener noreferrer">
            {renderInline(node.children)}
          </Link>
        );
    }
  });
}

const HEADING_ELEMENT = { 1: 'h3', 2: 'h4', 3: 'h5' } as const;
const HEADING_VARIANT = { 1: 'h6', 2: 'subtitle1', 3: 'subtitle2' } as const;

export function MarkdownPreview({ source }: { source: string }) {
  const blocks = parseMarkdownSubset(source);
  return (
    <Box sx={{ overflowWrap: 'anywhere', '& > :first-of-type': { mt: 0 } }}>
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          return (
            <Typography
              key={index}
              component={HEADING_ELEMENT[block.level]}
              variant={HEADING_VARIANT[block.level]}
              sx={{ mt: 2, mb: 1 }}
            >
              {renderInline(block.children)}
            </Typography>
          );
        }
        if (block.type === 'list') {
          return (
            <Box key={index} component={block.ordered ? 'ol' : 'ul'} sx={{ mt: 1, mb: 1, pl: 3 }}>
              {block.items.map((item, itemIndex) => (
                <Typography key={itemIndex} component="li" variant="body2">
                  {renderInline(item)}
                </Typography>
              ))}
            </Box>
          );
        }
        return (
          <Typography key={index} variant="body2" sx={{ mt: 1, mb: 1 }}>
            {renderInline(block.children)}
          </Typography>
        );
      })}
    </Box>
  );
}
